/**
 * Dialog de configuração do node Horário Comercial
 * Permite definir horários por dia da semana e fuso horário
 * 
 * RELEASE: 1.0.0 - 2025-01-22 - Novo dialog
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Clock, Calendar } from 'lucide-react';
import { FlowNode, BusinessHoursConfig, BusinessHoursDay } from '@/types/flows';
import { cn } from '@/lib/utils';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';

interface ConfigureBusinessHoursDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: BusinessHoursConfig) => void;
}

// Dias da semana com labels em português
const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Segunda-feira', short: 'Seg' },
  { key: 'tuesday', label: 'Terça-feira', short: 'Ter' },
  { key: 'wednesday', label: 'Quarta-feira', short: 'Qua' },
  { key: 'thursday', label: 'Quinta-feira', short: 'Qui' },
  { key: 'friday', label: 'Sexta-feira', short: 'Sex' },
  { key: 'saturday', label: 'Sábado', short: 'Sáb' },
  { key: 'sunday', label: 'Domingo', short: 'Dom' },
] as const;

// Fusos horários principais do Brasil
const TIMEZONES = [
  { value: 'America/Sao_Paulo', label: 'Brasília (GMT-3)' },
  { value: 'America/Manaus', label: 'Manaus (GMT-4)' },
  { value: 'America/Cuiaba', label: 'Cuiabá (GMT-4)' },
  { value: 'America/Rio_Branco', label: 'Rio Branco (GMT-5)' },
  { value: 'America/Noronha', label: 'Fernando de Noronha (GMT-2)' },
  { value: 'America/Belem', label: 'Belém (GMT-3)' },
  { value: 'America/Fortaleza', label: 'Fortaleza (GMT-3)' },
  { value: 'America/Recife', label: 'Recife (GMT-3)' },
  { value: 'America/Bahia', label: 'Salvador (GMT-3)' },
  { value: 'America/Campo_Grande', label: 'Campo Grande (GMT-4)' },
  { value: 'America/Boa_Vista', label: 'Boa Vista (GMT-4)' },
  { value: 'America/Porto_Velho', label: 'Porto Velho (GMT-4)' },
  { value: 'UTC', label: 'UTC (GMT+0)' },
];

// Configuração padrão de um dia
const DEFAULT_DAY: BusinessHoursDay = {
  enabled: false,
  start_time: '08:00',
  end_time: '18:00',
};

// Schedule padrão (segunda a sexta, 8h-18h)
const DEFAULT_SCHEDULE: BusinessHoursConfig['schedule'] = {
  sunday: { ...DEFAULT_DAY },
  monday: { ...DEFAULT_DAY, enabled: true },
  tuesday: { ...DEFAULT_DAY, enabled: true },
  wednesday: { ...DEFAULT_DAY, enabled: true },
  thursday: { ...DEFAULT_DAY, enabled: true },
  friday: { ...DEFAULT_DAY, enabled: true },
  saturday: { ...DEFAULT_DAY },
};

/**
 * Dialog de configuração do node Horário Comercial
 */
export function ConfigureBusinessHoursDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureBusinessHoursDialogProps) {
  const existingConfig = node.data.config as BusinessHoursConfig | undefined;
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  const [schedule, setSchedule] = useState<BusinessHoursConfig['schedule']>(
    existingConfig?.schedule || DEFAULT_SCHEDULE
  );
  const [timezone, setTimezone] = useState(
    existingConfig?.timezone || 'America/Sao_Paulo'
  );
  
  // Sincroniza estado quando node muda
  useEffect(() => {
    if (existingConfig?.schedule) {
      setSchedule(existingConfig.schedule);
    }
    if (existingConfig?.timezone) {
      setTimezone(existingConfig.timezone);
    }
  }, [existingConfig]);
  
  // Atualiza um dia específico
  const updateDay = (dayKey: string, updates: Partial<BusinessHoursDay>) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: { ...prev[dayKey as keyof typeof prev], ...updates },
    }));
  };
  
  // Presets rápidos
  const applyPreset = (preset: 'commercial' | '24h_weekdays' | 'all_week') => {
    const newSchedule = { ...schedule };
    
    switch (preset) {
      case 'commercial':
        // Segunda a sexta, 8h-18h
        DAYS_OF_WEEK.forEach(day => {
          newSchedule[day.key as keyof typeof newSchedule] = {
            enabled: !['saturday', 'sunday'].includes(day.key),
            start_time: '08:00',
            end_time: '18:00',
          };
        });
        break;
      case '24h_weekdays':
        // Segunda a sexta, 24h
        DAYS_OF_WEEK.forEach(day => {
          newSchedule[day.key as keyof typeof newSchedule] = {
            enabled: !['saturday', 'sunday'].includes(day.key),
            start_time: '00:00',
            end_time: '23:59',
          };
        });
        break;
      case 'all_week':
        // Todos os dias, 8h-18h
        DAYS_OF_WEEK.forEach(day => {
          newSchedule[day.key as keyof typeof newSchedule] = {
            enabled: true,
            start_time: '08:00',
            end_time: '18:00',
          };
        });
        break;
    }
    
    setSchedule(newSchedule);
  };
  
  // Salva configuração
  const handleSave = () => {
    const config: BusinessHoursConfig = {
      schedule,
      timezone,
    };
    onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as any);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Configurar Horário Comercial
          </DialogTitle>
          <DialogDescription>
            Defina os horários de funcionamento para cada dia da semana
          </DialogDescription>
        </DialogHeader>
        
        {/* Campo ID do Node e Apelido */}
        <NodeIdField ref={nicknameRef} nodeId={node.id} defaultNickname={node.data.config?.nickname} />
        
        {/* Presets rápidos */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Presets rápidos</Label>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => applyPreset('commercial')}
              className="text-xs"
            >
              Comercial (8h-18h Seg-Sex)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => applyPreset('24h_weekdays')}
              className="text-xs"
            >
              24h Seg-Sex
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => applyPreset('all_week')}
              className="text-xs"
            >
              7 dias (8h-18h)
            </Button>
          </div>
        </div>
        
        <Separator />
        
        {/* Tabela de horários */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Horários por dia
          </Label>
          
          <div className="space-y-2">
            {DAYS_OF_WEEK.map(day => {
              const dayConfig = schedule[day.key as keyof typeof schedule];
              
              return (
                <div 
                  key={day.key}
                  className={cn(
                    "flex items-center gap-3 p-2 rounded-lg border transition-colors",
                    dayConfig.enabled 
                      ? "bg-card border-border" 
                      : "bg-muted/30 border-transparent"
                  )}
                >
                  {/* Switch para habilitar/desabilitar */}
                  <Switch
                    checked={dayConfig.enabled}
                    onCheckedChange={(enabled) => updateDay(day.key, { enabled })}
                  />
                  
                  {/* Nome do dia */}
                  <span className={cn(
                    "w-24 text-sm font-medium",
                    !dayConfig.enabled && "text-muted-foreground"
                  )}>
                    {day.label}
                  </span>
                  
                  {/* Horários */}
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      type="time"
                      value={dayConfig.start_time}
                      onChange={(e) => updateDay(day.key, { start_time: e.target.value })}
                      disabled={!dayConfig.enabled}
                      className="w-[100px] text-xs"
                    />
                    <span className="text-muted-foreground text-xs">até</span>
                    <Input
                      type="time"
                      value={dayConfig.end_time}
                      onChange={(e) => updateDay(day.key, { end_time: e.target.value })}
                      disabled={!dayConfig.enabled}
                      className="w-[100px] text-xs"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <Separator />
        
        {/* Fuso horário */}
        <div className="space-y-2">
          <Label htmlFor="timezone">Fuso Horário</Label>
          <Select value={timezone} onValueChange={setTimezone}>
            <SelectTrigger id="timezone">
              <SelectValue placeholder="Selecione o fuso horário" />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map(tz => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            A comparação de horário será feita neste fuso horário
          </p>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
